import {
    ApiResponse,
    approveCampaignManagerBookingToBill,
    BookingStatus,
    CampaignManagerBooking,
    CampaignManagerBookingTotals,
    CampaignManagerInfluencer,
    confirmCampaignManagerBooking,
    createAndSendCampaignManagerInvoice,
    CreateAndSendCampaignManagerInvoiceData,
    declineCampaignManagerBooking,
    getAllCampaignManagerInfluencers,
    getCampaignManagerBooking,
    getCampaignManagerBookings,
    patchCampaignManagerBooking,
    PatchCampaignManagerBookingData,
    submitCampaignManagerBookingStatus,
} from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import { mapApiErrorsToFormikErrors } from '../../../utils/utits';
import debounce from 'lodash/debounce';
import { showNotification } from '../../../utils/showNotification';
import uniq from 'lodash/uniq';

type OperationResult = { success: boolean; reason?: string };

export type UseCampaignManagerBookingsResult = {
    bookings: CampaignManagerBooking[];
    totals: CampaignManagerBookingTotals | null;
    influencers: CampaignManagerInfluencer[];
    isInitialized: boolean;
    isLoading: boolean;
    hasErrorLoadingData: boolean;
    hasNextPage: boolean;

    init: (requestInit?: RequestInit) => Promise<void>;
    reset: () => void;
    loadNextPage: (requestInit?: RequestInit) => Promise<void>;
    editBooking: (bookingId: number, data: PatchCampaignManagerBookingData) => Promise<OperationResult>;
    addBooking: (bookingId: number) => Promise<OperationResult>;
    setStatus: (bookingId: number, status: BookingStatus) => Promise<OperationResult>;
    approveToBill: (
        bookingId: number,
        data: {
            create_invoice: boolean;
            discount?: number;
        }
    ) => Promise<OperationResult>;
    createAndSendInvoice: (data: CreateAndSendCampaignManagerInvoiceData) => Promise<OperationResult>;
};

type UseCampaignManagerBookingsParams = Pick<
    Parameters<typeof getCampaignManagerBookings>[0],
    | 'page_size'
    | 'included_statuses'
    | 'excluded_statuses'
    | 'influencer_id'
    | 'campaign_id'
    | 'my_bookings'
    | 'client_id'
    | 'date_created_start'
    | 'date_created_end'
    | 'date_approved_start'
    | 'date_approved_end'
>;

export function useCampaignManagerBookings({
    page_size,
    included_statuses,
    excluded_statuses,
    influencer_id,
    campaign_id,
    my_bookings,
    client_id,
    date_created_start,
    date_created_end,
    date_approved_start,
    date_approved_end,
}: UseCampaignManagerBookingsParams): UseCampaignManagerBookingsResult {
    const [page, setPage] = useState(1);

    const [bookings, setBookings] = useState<CampaignManagerBooking[]>([]);
    const [totals, setTotals] = useState<CampaignManagerBookingTotals | null>(null);
    const [influencers, setInfluencers] = useState<CampaignManagerInfluencer[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoadingData, setHasErrorLoadingData] = useState(false);

    const fetchInfluencers = useCallback(async (...params: Parameters<typeof getAllCampaignManagerInfluencers>) => {
        try {
            const influencers = await getAllCampaignManagerInfluencers(...params);
            setInfluencers((prev) => prev.concat(influencers));
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            showNotification('Error loading influencers', 'error');
        }
    }, []);

    const fetchBookingsData = useCallback(
        async (...[params, requestInit]: Parameters<typeof getCampaignManagerBookings>) => {
            try {
                setIsLoading(true);
                setHasErrorLoadingData(false);
                const response = await getCampaignManagerBookings(params, requestInit);
                if (response.status === 200) {
                    setBookings((bookings) => bookings.concat(response.data.results));
                    setTotals(response.data.totals);
                    setHasNextPage(Boolean(response.data.next));

                    const influencerIdsInResponse = uniq(
                        response.data.results.map((booking) => booking.influencer_id).filter((id): id is number => !!id)
                    );

                    const influencerIdsInState = influencers.map((influencer) => influencer.id);

                    const influencerIdsToFetch = influencerIdsInResponse.filter(
                        (influencerId) => !influencerIdsInState.includes(influencerId)
                    );

                    if (influencerIdsToFetch.length) {
                        await fetchInfluencers(influencerIdsToFetch.join(','), requestInit);
                    }

                    setIsInitialized(true);
                    return;
                }

                setHasErrorLoadingData(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoadingData(true);
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
        [fetchInfluencers, influencers]
    );

    const init = useCallback(
        async (requestInit?: RequestInit) => {
            await fetchBookingsData(
                {
                    page,
                    page_size,
                    included_statuses,
                    excluded_statuses,
                    influencer_id,
                    campaign_id,
                    my_bookings,
                    client_id,
                    date_created_start,
                    date_created_end,
                    date_approved_start,
                    date_approved_end,
                },
                requestInit
            );
        },
        [
            excluded_statuses,
            fetchBookingsData,
            included_statuses,
            influencer_id,
            campaign_id,
            my_bookings,
            client_id,
            date_created_start,
            date_created_end,
            date_approved_start,
            date_approved_end,
            page,
            page_size,
        ]
    );

    const reset = useCallback(() => {
        setBookings([]);
        setTotals(null);
        setInfluencers([]);
        setPage(1);
        setHasNextPage(false);
        setIsInitialized(false);
        setIsLoading(false);
        setHasErrorLoadingData(false);
    }, []);

    const loadNextPage = useCallback(
        async (requestInit?: RequestInit) => {
            await fetchBookingsData(
                {
                    page: page + 1,
                    page_size,
                    included_statuses,
                    excluded_statuses,
                    influencer_id,
                    campaign_id,
                    my_bookings,
                    client_id,
                    date_created_start,
                    date_created_end,
                    date_approved_start,
                    date_approved_end,
                },

                requestInit
            );
            setPage((page) => page + 1);
        },
        [
            excluded_statuses,
            fetchBookingsData,
            included_statuses,
            influencer_id,
            campaign_id,
            my_bookings,
            client_id,
            date_created_start,
            date_created_end,
            date_approved_start,
            date_approved_end,
            page,
            page_size,
        ]
    );

    const debouncedLoadNextPage = useMemo(
        () => debounce(loadNextPage, 500, { leading: true, trailing: false }),
        [loadNextPage]
    );

    const refreshBooking = useCallback(async (bookingId: number): Promise<OperationResult> => {
        const response = await getCampaignManagerBooking(bookingId);
        if (response.status === 404) {
            return {
                success: false,
                reason: 'Booking not found',
            };
        }

        setBookings((bookings) =>
            bookings.map((booking) => (booking.id === response.data.id ? response.data : booking))
        );

        return { success: true };
    }, []);

    const addBooking: UseCampaignManagerBookingsResult['addBooking'] = useCallback(async (bookingId) => {
        const response = await getCampaignManagerBooking(bookingId);
        if (response.status === 200) {
            setBookings((bookings) => [response.data, ...bookings]);
            return { success: true };
        }

        return { success: false, reason: 'Booking not found' };
    }, []);

    const editBooking: UseCampaignManagerBookingsResult['editBooking'] = useCallback(
        async (bookingId, data) => {
            const response = await patchCampaignManagerBooking(bookingId, data);
            if (response.status !== 200) {
                const errorMessage =
                    response.status === 404
                        ? response.data.detail
                        : Object.values(mapApiErrorsToFormikErrors(response.data))[0] || 'Could not update booking';
                return { success: false, reason: errorMessage };
            }

            const refreshResult = await refreshBooking(bookingId);
            if (!refreshResult.success) {
                return { success: false, reason: 'Booking updated, but could not refresh data' };
            }

            return { success: true };
        },
        [refreshBooking]
    );

    const setStatus: UseCampaignManagerBookingsResult['setStatus'] = useCallback(
        async (bookingId, status) => {
            const statusOperations: {
                [key in BookingStatus]?: (bookingId: number) => Promise<ApiResponse<any>>;
            } = {
                confirmed: confirmCampaignManagerBooking,
                declined: declineCampaignManagerBooking,
                draft_approved: (bookingId: number) => submitCampaignManagerBookingStatus(bookingId, 'draft_approved'),
                draft_sent: (bookingId: number) => submitCampaignManagerBookingStatus(bookingId, 'draft_sent'),
                content_only: (bookingId: number) => submitCampaignManagerBookingStatus(bookingId, 'content_only'),
                redrafting: (bookingId: number) => submitCampaignManagerBookingStatus(bookingId, 'redrafting'),
                voided: (bookingId: number) => submitCampaignManagerBookingStatus(bookingId, 'voided'),
            };

            const operation = statusOperations[status];
            if (!operation) {
                return { success: false, reason: 'Operation is not supported' };
            }

            const response = await operation(bookingId);
            if (response.status >= 300) {
                return { success: false, reason: 'Could not update status' };
            }

            const refreshResults = await refreshBooking(bookingId);
            if (!refreshResults.success) {
                return {
                    success: false,
                    reason: 'Status updated, but could not refresh booking data',
                };
            }

            return { success: true };
        },
        [refreshBooking]
    );

    const approveToBill: UseCampaignManagerBookingsResult['approveToBill'] = useCallback(
        async (bookingId, data) => {
            const response = await approveCampaignManagerBookingToBill(bookingId, data);
            if (response.status === 404) {
                return { success: false, reason: 'Booking not found' };
            }

            const refreshResult = await refreshBooking(bookingId);
            if (!refreshResult.success) {
                return { success: false, reason: 'Approved to bill, but could not refresh booking data' };
            }

            return { success: true };
        },
        [refreshBooking]
    );

    const createAndSendInvoice = useCallback(async (data: CreateAndSendCampaignManagerInvoiceData) => {
        const response = await createAndSendCampaignManagerInvoice(data);

        if (response.status === 201) {
            return { success: true };
        }

        if (response.status === 400) {
            const errorMessage = Array.isArray(response.data) ? response.data.join(', ') : response.data;
            return { success: false, reason: errorMessage };
        }

        return { success: false, reason: "Couldn't create and send invoice" };
    }, []);

    return useMemo(
        () => ({
            bookings,
            totals,
            influencers,
            isLoading,
            hasErrorLoadingData,
            hasNextPage,
            init,
            reset,
            isInitialized,
            loadNextPage: debouncedLoadNextPage,
            addBooking,
            editBooking,
            setStatus,
            approveToBill,
            createAndSendInvoice,
        }),
        [
            approveToBill,
            bookings,
            influencers,
            editBooking,
            addBooking,
            hasErrorLoadingData,
            hasNextPage,
            init,
            reset,
            isInitialized,
            isLoading,
            debouncedLoadNextPage,
            setStatus,
            totals,
            createAndSendInvoice,
        ]
    );
}
